import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, CATEGORIES, catLabel, imgSrc, imageRatioClass, imagePositionClass, waList } from "@/lib/api";
import WhatsAppPicker from "@/components/WhatsAppPicker";
import { MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/seo";

export default function Catalog() {
  usePageMeta("Katalog Tali, Katrol & Timbangan", "Katalog Tali PP LUMBA LUMBA & KUDA EMAS, Katrol LUMBA LUMBA, dan Timbangan Manual & Digital — tanya harga langsung via WhatsApp.");
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState({});
  const { user } = useAuth();
  const [ask, setAsk] = useState(null);

  useEffect(() => {
    setLoading(true);
    API.get("/products", { params: cat ? { category: cat } : {} })
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cat]);

  useEffect(() => {
    API.get("/settings").then((response) => setSite(response.data || {})).catch(() => {});
  }, []);

  return (
    <div className="bg-navy-dark text-white min-h-screen">
      <section className="relative py-20 border-b border-gold/20 bg-maritime-grid noise-overlay" data-testid="catalog-header">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 15% 0%, rgba(201,162,39,0.12) 0%, transparent 55%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Katalog Produk</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Produk Unggulan Kami</h1>
          <p className="mt-4 text-slate-400 text-sm sm:text-base max-w-2xl">
            Daftar lengkap Tali Tambang, Katrol, dan Timbangan kami. Untuk informasi harga, silakan tanya langsung via
            WhatsApp. Akses toko online hanya untuk akun yang dibuatkan oleh pemilik.
          </p>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="catalog-filters">
            <button
              onClick={() => setCat("")}
              data-testid="filter-all"
              className={`px-5 py-2 text-sm font-semibold rounded-full border transition-colors ${!cat ? "bg-gold text-[#0B1F3A] border-gold" : "border-white/20 text-slate-300 hover:border-gold hover:text-gold"}`}
            >
              Semua
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                onClick={() => setCat(c.value)}
                data-testid={`filter-${c.value}`}
                className={`px-5 py-2 text-sm font-semibold rounded-full border transition-colors ${cat === c.value ? "bg-gold text-[#0B1F3A] border-gold" : "border-white/20 text-slate-300 hover:border-gold hover:text-gold"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-16 bg-rope-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {loading ? (
            <p className="text-slate-400" data-testid="catalog-loading">Memuat produk...</p>
          ) : products.length === 0 ? (
            <p className="text-slate-400" data-testid="catalog-empty">Belum ada produk pada kategori ini.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalog-grid">
              {products.map((p) => (
                <article key={p.id} data-testid={`catalog-product-${p.id}`} className="card-maritime-surface rounded-md overflow-hidden hover:-translate-y-1 hover:border-gold/50 transition-transform">
                  <div className={`${imageRatioClass(p.image_ratios?.[0], "1:1")} overflow-hidden bg-white`}>
                    <img src={imgSrc((p.images || [])[0])} alt={p.name} className={`w-full h-full object-cover ${imagePositionClass(p.image_positions?.[0])}`} />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">{p.brand || "NAWADAH by NDH Official"}</span>
                      <span className="text-[10px] uppercase tracking-widest text-slate-500">• {catLabel(p.category)}</span>
                    </div>
                    <h3 className="font-bold text-base mb-2">{p.name}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed line-clamp-3 mb-4">{p.description}</p>
                    {p.variants?.length > 0 && (
                      <p className="text-xs text-slate-500 mb-4">Varian: {p.variants.map((v) => v.label).join(", ")}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAsk(p)}
                        data-testid={`ask-price-${p.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gold bg-gold-hover text-[#0B1F3A] text-sm font-bold rounded-md transition-colors"
                      >
                        <MessageCircle className="h-4 w-4" /> Tanya Harga
                      </button>
                      {user && (
                        <Link to={`/toko/${p.id}`} data-testid={`view-shop-${p.id}`} className="px-4 py-2.5 border border-white/20 text-sm font-semibold rounded-md hover:border-gold hover:text-gold transition-colors">
                          Lihat
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <WhatsAppPicker
        open={!!ask}
        onClose={() => setAsk(null)}
        contacts={waList(site)}
        message={ask ? `Halo Admin NAWADAH, saya ingin menanyakan harga dan ketersediaan: ${ask.name}` : ""}
      />
    </div>
  );
}
